export default customElements.define('w-investment-diamond', class InvestmentDiamond extends wealthify.CustomElementBaseClass {
    constructor() {
        const styleSelector = 'c-investment-diamond';
        const config = {
            styleSelector,
            templateHTML: `
            <div class="${styleSelector}__container" data-root>
                <w-loading-spinner absolute="true" colour="black" data-loadingspinner></w-loading-spinner>
                <div class="${styleSelector}__content" aria-hidden="true" data-validcontent>
                    <p class="${styleSelector}__content-initial-title" data-initialtitle></p>
                    <p class="${styleSelector}__content-initial-value" data-initialinvestment></p>
                    <p class="${styleSelector}__content-tax-relief" data-taxrelief></p>
                    <p class="${styleSelector}__content-projected-title" data-projectedtitle></p>
                    <p class="${styleSelector}__content-projected-value" data-projected></p>
                    <p class="${styleSelector}__content-projected-footer" data-projectedfooter></p>
                </div>

                <div class="${styleSelector}__content u-display__none" aria-hidden="true" data-invalidcontent>
                    <p class="${styleSelector}__content-max-contribution-title">Reduce your <br> contribution</p>
                    <p class="${styleSelector}__content-max-contribution-value u-margin__bottom-mobile--0">You can <br> contribute up <br> to 
                        <span class="${styleSelector}__content-max-contribution-value" data-maxallowance></span>
                    </p>
                </div>

                <div class="u-display__hidden" aria-live="polite" data-arialive></div>
            </div>`,
            cacheDom: [ 
                'data-validcontent', 
                'data-invalidcontent', 
                'data-initialinvestment', 
                'data-loadingspinner', 
                'data-projected', 
                'data-projectedfooter', 
                'data-taxrelief', 
                'data-maxallowance', 
                'data-arialive',  
                'data-initialtitle', 
                'data-projectedtitle' 
            ],
            hasChildren: true
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);

            this._calculateDiamondSize();
            
            this.dom.validcontent.addEventListener ('DOMSubtreeModified', () => {
                this._calculateDiamondSize();
            });
        };
    }

    static get validAccountTypes () {
        return [
            'sipp'
        ];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('accounttype', {type: wealthify.propTypes.ArrayItem(this.validAccountTypes)}),

            new wealthify.Prop('includetaxrelief', {default: false}),

            new wealthify.Prop('initialinvestmentvalue', {
                default: 0,
                type: wealthify.propTypes.Number()
            }),

            new wealthify.Prop('initialtitle', { default: 'You Invest' }),

            new wealthify.Prop('label', {required: true}),

            new wealthify.Prop('loading', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),

            new wealthify.Prop('offsettop', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),

            new wealthify.Prop('projectedvalue', {
                default: 0,
                type: wealthify.propTypes.Number()
            }),

            new wealthify.Prop('projectedfooter'),

            new wealthify.Prop('projectedtitle', { default: 'Projected Value' }),

            new wealthify.Prop('size'),

            new wealthify.Prop('taxreliefvalue', {
                default: 0,
                type: wealthify.propTypes.Number()
            }),

            new wealthify.Prop('maxvalue', {
                type: wealthify.propTypes.Number(),
                nullable: true
            })
        ];
    }

    get propCallbacks() {
        return {
            accounttype: (value) => {
                const classListAction = value === 'sipp' ? 'remove' : 'add';

                this.dom.projectedfooter.classList[classListAction]('u-display__none');
                this.dom.projectedfooter.innerHTML = 'At retirement';

                console.group('Deprecated props');
                console.warn('The accounttype prop is deprecated on the Investment Diamond component. Please use initialtitle, projectedtitle and projectedfooter');
                console.groupEnd();
            },

            includetaxrelief: (value) => {
                const classListAction = value ? 'remove' : 'add';

                this.dom.taxrelief.classList[classListAction]('u-display__none');
            },

            initialinvestmentvalue: (value) => {
                this.dom.initialinvestment.innerHTML = wealthify.type.number.toCurrency(value);
                this._checkAgainstMaxAllowance();
            },

            initialtitle: (value) => {
                this.dom.initialtitle.innerHTML = value;
            },

            label: (value) => this.setAttribute('aria-label', value),

            loading: (loading) => {
                const spinnerClassListAction = loading ? 'remove' : 'add';
                const contentClassListAction = loading ? 'add' : 'remove';

                this.dom.loadingspinner.classList[spinnerClassListAction]('u-display__none');
                this.dom.validcontent.classList[contentClassListAction](`${this.styleSelector}--hidden`);
            },

            offsettop: () => {
                this._calculateDiamondSize();
            },

            projectedfooter: (value) => {
                this.dom.projectedfooter.innerHTML = value;
            },

            projectedtitle: (value) => {
                this.dom.projectedtitle.innerHTML = value;
            },

            projectedvalue: (value) => {
                const projectValueFormatted = wealthify.type.number.toCurrency(value);

                this.dom.projected.innerHTML = projectValueFormatted;
                this.dom.arialive.textContent = `Projected value is ${projectValueFormatted} ${this.dom.projectedfooter.innerHTML}`;
            },

            size: () => {
                this._calculateDiamondSize();
            },

            taxreliefvalue: (value) => {
                this.dom.taxrelief.innerHTML = `Plus ${wealthify.type.number.toCurrency(value, false)} tax relief`;
            },

            maxvalue: () => {
                this.dom.maxallowance.innerHTML = wealthify.type.number.toCurrency(this.maxvalue); 
                this._checkAgainstMaxAllowance();  
            }
        };
    }

    _calculateDiamondSize() {
        const contentLength = this.dom.validcontent.offsetHeight > this.dom.validcontent.offsetWidth ? this.dom.validcontent.offsetHeight : this.dom.validcontent.offsetWidth;
        const diamondLength = this.size !== undefined ? this.size : contentLength + 40;
        const hypotenuse = Math.hypot(diamondLength, diamondLength);

        this.dom.root.style.width = this.dom.loadingspinner.style.width = `${diamondLength}px`;
        this.dom.root.style.height = this.dom.loadingspinner.style.height = `${diamondLength}px`;
        this.style.width = `${hypotenuse}px`;
        this.style.height = `${hypotenuse}px`;

        if (this.offsettop) {
            this._setOffset(hypotenuse);
        }
    }

    _setOffset(hypotenuse) {
        this.style.position = 'absolute';
        this.style.top = `-${hypotenuse / 2 }px`;
        this.parentElement.style.marginTop = `${hypotenuse / 2}px`;
    }

    _checkAgainstMaxAllowance(){

        if(this.maxvalue === undefined || this.initialinvestmentvalue === undefined){
            return;
        }

        const initialInvestmentExceedsMax = this.initialinvestmentvalue > this.maxvalue;
        const validClassListAction = initialInvestmentExceedsMax ? 'add' : 'remove';
        const invalidClassListAction = initialInvestmentExceedsMax ? 'remove' : 'add';

        this.dom.root.classList[validClassListAction](`${this.styleSelector}__container--invalid`);
        this.dom.validcontent.classList[validClassListAction]('u-display__none');
        this.dom.invalidcontent.classList[invalidClassListAction]('u-display__none');
        
    }

});